// Import React helpers.
import {
    useEffect,
    useRef,
    useState,
} from "react";



// Export the reusable scrolling-section hook.
export const UseScrollingSections = ({
    sections = [],
    getSectionKey = (section) => section?.section_key,
}) => {

    // Store the section which is currently active.
    const [activeSectionKey, setActiveSectionKey] = useState("");

    // Store a manually selected section so observer updates can be temporarily paused.
    const [manualActiveSectionKey, setManualActiveSectionKey] = useState("");

    // Store a reference to the independently scrollable navigation list.
    const navigationScrollRef = useRef(null);



    // Track which content section occupies the largest visible area.
    useEffect(() => {

        // Stop when there are no sections to inspect.
        if (!Array.isArray(sections) || sections.length === 0) {

            // Exit the effect.
            return undefined;

        }



        // Store the current animation frame so repeated scroll events can be throttled.
        let animationFrameId = null;



        // Calculate which section should currently be active.
        const calculateActiveSection = () => {

            // Clear the stored animation frame because this calculation is now running.
            animationFrameId = null;



            // Do not change the active section while the user has manually selected one.
            if (manualActiveSectionKey) {

                // Exit the calculation.
                return;

            }



            // Store the rendered section elements in their supplied order.
            const sectionElements = sections
                .map((section) => {

                    // Get this section's unique key.
                    const sectionKey = getSectionKey(section);

                    // Find the matching rendered section element.
                    const sectionElement = document.getElementById(sectionKey);

                    // Return the key and rendered element together.
                    return {
                        sectionKey,
                        sectionElement,
                    };

                })
                .filter((section) => {

                    // Only keep sections which exist in the document.
                    return Boolean(section.sectionKey && section.sectionElement);

                });



            // Stop when none of the supplied sections exist in the document.
            if (sectionElements.length === 0) {

                // Exit the calculation.
                return;

            }



            // Get the first rendered section.
            const firstSection = sectionElements[0];

            // Get the first section's position relative to the viewport.
            const firstSectionBounds =
                firstSection.sectionElement.getBoundingClientRect();



            // Check whether the page is currently above the first content section.
            if (firstSectionBounds.top > 0) {

                // Keep the first navigation item highlighted.
                setActiveSectionKey(firstSection.sectionKey);

                // Stop before comparing the remaining sections.
                return;

            }



            // Get the current viewport height.
            const viewportHeight =
                window.innerHeight
                || document.documentElement.clientHeight;



            // Store the section occupying the most visible vertical space.
            let mostVisibleSectionKey = firstSection.sectionKey;

            // Store the largest visible height found so far.
            let largestVisibleHeight = 0;



            // Compare every section currently rendered on the page.
            sectionElements.forEach((section) => {

                // Get this section's position relative to the viewport.
                const sectionBounds =
                    section.sectionElement.getBoundingClientRect();

                // Get the visible upper edge of the section.
                const visibleTop = Math.max(
                    sectionBounds.top,
                    0
                );

                // Get the visible lower edge of the section.
                const visibleBottom = Math.min(
                    sectionBounds.bottom,
                    viewportHeight
                );

                // Calculate how many vertical pixels of the section are currently visible.
                const visibleHeight = Math.max(
                    0,
                    visibleBottom - visibleTop
                );



                // Check whether this section occupies more of the viewport.
                if (visibleHeight > largestVisibleHeight) {

                    // Store this section as the current winner.
                    mostVisibleSectionKey = section.sectionKey;

                    // Store the newly discovered visible height.
                    largestVisibleHeight = visibleHeight;

                }

            });



            // Update the highlighted navigation section.
            setActiveSectionKey(mostVisibleSectionKey);

        };



        // Handle browser scrolling and resizing.
        const handleViewportChange = () => {

            // Do not queue another calculation when one is already waiting.
            if (animationFrameId !== null) {

                // Exit the handler.
                return;

            }

            // Run the calculation on the browser's next animation frame.
            animationFrameId = window.requestAnimationFrame(
                calculateActiveSection
            );

        };



        // Calculate the correct section immediately.
        calculateActiveSection();



        // Recalculate while the page scrolls.
        window.addEventListener(
            "scroll",
            handleViewportChange,
            {
                passive: true,
            }
        );

        // Recalculate when the browser size changes.
        window.addEventListener(
            "resize",
            handleViewportChange
        );



        // Clean up when the hook changes or unmounts.
        return () => {

            // Remove the scroll listener.
            window.removeEventListener(
                "scroll",
                handleViewportChange
            );

            // Remove the resize listener.
            window.removeEventListener(
                "resize",
                handleViewportChange
            );

            // Cancel a waiting animation frame when one exists.
            if (animationFrameId !== null) {

                // Cancel the queued browser calculation.
                window.cancelAnimationFrame(animationFrameId);

            }

        };

    }, [
        sections,
        getSectionKey,
        manualActiveSectionKey,
    ]);



    // Release a manually selected section when the user starts scrolling themselves.
    useEffect(() => {

        // Stop when there is no manually selected section.
        if (!manualActiveSectionKey) {

            // Exit the effect.
            return undefined;

        }

        // Store whether the user has initiated their own scroll interaction.
        let userStartedScrolling = false;



        // Mark that the user initiated a scroll action.
        const handleUserScrollIntent = () => {

            // Record the manual scroll intent.
            userStartedScrolling = true;

        };



        // Release the manual section lock once an actual scroll follows.
        const handleScroll = () => {

            // Ignore programmatic smooth scrolling.
            if (!userStartedScrolling) {

                // Exit the handler.
                return;

            }

            // Return control to the section observer.
            setManualActiveSectionKey("");

        };



        // Listen for mouse wheel scrolling.
        window.addEventListener(
            "wheel",
            handleUserScrollIntent,
            {
                passive: true,
            }
        );

        // Listen for touch scrolling.
        window.addEventListener(
            "touchmove",
            handleUserScrollIntent,
            {
                passive: true,
            }
        );

        // Listen for keyboard scrolling.
        window.addEventListener(
            "keydown",
            handleUserScrollIntent
        );

        // Listen for actual page scrolling.
        window.addEventListener(
            "scroll",
            handleScroll,
            {
                passive: true,
            }
        );

        // Remove all event listeners when the lock changes or the hook unmounts.
        return () => {

            // Remove the mouse wheel listener.
            window.removeEventListener(
                "wheel",
                handleUserScrollIntent
            );

            // Remove the touch listener.
            window.removeEventListener(
                "touchmove",
                handleUserScrollIntent
            );

            // Remove the keyboard listener.
            window.removeEventListener(
                "keydown",
                handleUserScrollIntent
            );

            // Remove the page scroll listener.
            window.removeEventListener(
                "scroll",
                handleScroll
            );

        };

    }, [manualActiveSectionKey]);



    // Keep the active navigation item visible in the left-hand scrolling list.
    useEffect(() => {

        // Stop when there is no active section.
        if (!activeSectionKey) {

            // Exit the effect.
            return;

        }

        // Get the scrollable navigation element.
        const navigationScrollElement = navigationScrollRef.current;

        // Stop when the navigation element has not mounted.
        if (!navigationScrollElement) {

            // Exit the effect.
            return;

        }

        // Find the navigation item matching the active section.
        const activeNavigationLink =
            navigationScrollElement.querySelector(
                `[data-section-key="${activeSectionKey}"]`
            );

        // Stop when the matching navigation item cannot be found.
        if (!activeNavigationLink) {

            // Exit the effect.
            return;

        }

        // Get the visible navigation boundaries.
        const navigationBounds =
            navigationScrollElement.getBoundingClientRect();

        // Get the active navigation item boundaries.
        const activeLinkBounds =
            activeNavigationLink.getBoundingClientRect();

        // Determine whether the item has disappeared above the scroll area.
        const activeLinkIsAbove =
            activeLinkBounds.top < navigationBounds.top;

        // Determine whether the item has disappeared below the scroll area.
        const activeLinkIsBelow =
            activeLinkBounds.bottom > navigationBounds.bottom;

        // Only reposition the navigation when the active item is hidden.
        if (activeLinkIsAbove || activeLinkIsBelow) {

            // Move the active item into view.
            activeNavigationLink.scrollIntoView({

                // Animate the navigation movement.
                behavior: "smooth",

                // Keep the active item near the middle.
                block: "center",

                // Prevent unnecessary horizontal movement.
                inline: "nearest",

            });

        }

    }, [activeSectionKey]);



    // Handle a section being explicitly selected from navigation.
    const handleSectionSelect = (sectionKey) => {

        // Stop when there is no valid key.
        if (!sectionKey) {

            // Exit the function.
            return;

        }

        // Lock the selected section as manually active.
        setManualActiveSectionKey(sectionKey);

        // Highlight the selected section immediately.
        setActiveSectionKey(sectionKey);

        // Find the matching page section.
        const sectionElement = document.getElementById(sectionKey);

        // Stop when the section cannot be found.
        if (!sectionElement) {

            // Exit the function.
            return;

        }

        // Scroll to the selected section.
        sectionElement.scrollIntoView({

            // Animate the page movement.
            behavior: "smooth",

            // Position the section near the top.
            block: "start",

        });

    };



    // Return the reusable scrolling-section functionality.
    return {

        // Return the currently highlighted section.
        activeSectionKey,

        // Return the section selection handler.
        handleSectionSelect,

        // Return the navigation scroll reference.
        navigationScrollRef,

    };

};